import { Organization } from '@klear/organization'
import type { InternationalExternalFinancialAccountMeta } from '@klear/international-external-financial-account/schema'

export type InternationalExternalFinancialAccountIdentity = { kind: 'new' } | { kind: 'saved'; id: number }

export type InternationalExternalFinancialAccountJson = {
  identity: InternationalExternalFinancialAccountIdentity
  organizationId: number
  meta: InternationalExternalFinancialAccountMeta
  organizationName?: string
  createdAt: number
  updatedAt: number
  deletedAt?: number
  createdByUserId: number
  createdByUserEmail: string
  updatedByUserId?: number
  updatedByUserEmail?: string
  deletedByUserId?: number
  deletedByUserEmail?: string
}

export function internationalExternalFinancialAccountToJson(
  account: InternationalExternalFinancialAccount
): InternationalExternalFinancialAccountJson {
  return {
    identity: account.identity,
    organizationId: account.organizationId,
    meta: account.meta,
    organizationName: account.organization.name || undefined,
    createdAt: account.createdAt,
    updatedAt: account.updatedAt,
    deletedAt: account.deletedAt,
    createdByUserId: account.createdByUserId,
    createdByUserEmail: account.createdByUserEmail,
    updatedByUserId: account.updatedByUserId,
    updatedByUserEmail: account.updatedByUserEmail,
    deletedByUserId: account.deletedByUserId,
    deletedByUserEmail: account.deletedByUserEmail
  }
}

export class InternationalExternalFinancialAccount {
  static readonly __klearClass = 'InternationalExternalFinancialAccount'

  readonly identity: InternationalExternalFinancialAccountIdentity
  organizationId: number
  meta: InternationalExternalFinancialAccountMeta
  organization: Organization
  createdAt: number
  updatedAt: number
  deletedAt?: number
  createdByUserId: number
  createdByUserEmail: string
  updatedByUserId?: number
  updatedByUserEmail?: string
  deletedByUserId?: number
  deletedByUserEmail?: string

  constructor(props: InternationalExternalFinancialAccountJson) {
    this.identity = props.identity
    this.organizationId = props.organizationId
    this.meta = props.meta
    this.organization = new Organization({ name: props.organizationName ?? '' })
    this.createdAt = props.createdAt
    this.updatedAt = props.updatedAt
    this.deletedAt = props.deletedAt
    this.createdByUserId = props.createdByUserId
    this.createdByUserEmail = props.createdByUserEmail
    this.updatedByUserId = props.updatedByUserId
    this.updatedByUserEmail = props.updatedByUserEmail
    this.deletedByUserId = props.deletedByUserId
    this.deletedByUserEmail = props.deletedByUserEmail
  }

  get id(): number | undefined {
    return this.identity.kind === 'saved' ? this.identity.id : undefined
  }

  readonly isKlearAccount = false

  get accountDetails() {
    return this.meta.accountDetails
  }

  set accountDetails(value: InternationalExternalFinancialAccountMeta['accountDetails']) {
    this.meta.accountDetails = value
  }

  get billingDetails() {
    return this.meta.billingDetails
  }

  set billingDetails(value: InternationalExternalFinancialAccountMeta['billingDetails']) {
    this.meta.billingDetails = value
  }

  get supportedPaymentNetworks() {
    return this.meta.supportedPaymentNetworks
  }

  set supportedPaymentNetworks(value: InternationalExternalFinancialAccountMeta['supportedPaymentNetworks']) {
    this.meta.supportedPaymentNetworks = value
  }

  get beneficiaryCreateInput() {
    return this.meta.beneficiaryCreateInput
  }

  set beneficiaryCreateInput(value: InternationalExternalFinancialAccountMeta['beneficiaryCreateInput']) {
    this.meta.beneficiaryCreateInput = value
  }

  get monexBeneficiary() {
    return this.meta.monexBeneficiary
  }

  set monexBeneficiary(value: InternationalExternalFinancialAccountMeta['monexBeneficiary']) {
    this.meta.monexBeneficiary = value
  }

  get name() {
    return this.accountDetails?.name ?? ''
  }

  get last4() {
    return this.accountDetails?.accountNumber?.slice(-4) ?? ''
  }

  get nameDetailed() {
    return `${this.name} - ${this.last4}`
  }

  get nameAndLast4() {
    return `${this.name} - ${this.last4}`
  }

  get accountHolderName() {
    return this.billingDetails?.name ?? ''
  }

  get accountNumber() {
    return this.accountDetails?.accountNumber ?? ''
  }

  get routingNumber() {
    return this.accountDetails?.routingCode ?? ''
  }

  get bankName() {
    return this.accountDetails?.name ?? ''
  }

  toJSON() {
    return internationalExternalFinancialAccountToJson(this)
  }
}
